import { resolveAppColorKey, resolveBrandColorHex } from "./appBrandColors";

/** Muted, dark-theme-friendly palette aligned with demo usage colors. */
export const APP_USAGE_PALETTE = [
  "#7ab89a",
  "#c28f65",
  "#8fa7c2",
  "#be7777",
  "#7b837e",
  "#9a8bc2",
  "#7ab0b8",
  "#b8a07a",
] as const;

export type AppColorInput = {
  appIdentifier?: string | null;
  displayName: string;
  color?: string | null;
};

export function appColorKey(app: AppColorInput): string {
  const id = app.appIdentifier?.trim();
  if (id) return id;
  const name = app.displayName.trim();
  return name || "unknown";
}

export function normalizeAppColor(color?: string | null): string | undefined {
  if (!color?.trim()) return undefined;
  const hex = color.trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return undefined;
  return hex.toLowerCase();
}

function stableHash(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (value: number) => Math.max(0, Math.min(255, Math.round(value)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")}`;
}

function adjustHexLightness(hex: string, delta: number): string {
  const [r, g, b] = hexToRgb(hex);
  if (delta >= 0) {
    return rgbToHex(r + (255 - r) * delta, g + (255 - g) * delta, b + (255 - b) * delta);
  }
  const factor = 1 + delta;
  return rgbToHex(r * factor, g * factor, b * factor);
}

function catalogKeysSharingHex(brandMap: Readonly<Record<string, string>>, hex: string): string[] {
  const normalized = hex.toLowerCase();
  return Object.keys(brandMap)
    .filter((key) => brandMap[key]!.toLowerCase() === normalized)
    .sort();
}

/** Deterministic per-app tweak when multiple catalog keys share the same brand hex. */
function brandColorVariant(baseHex: string, seed: string): string {
  const hash = stableHash(seed);
  const delta = ((hash % 17) - 8) / 100;
  const adjusted = adjustHexLightness(baseHex, delta);
  if (adjusted === baseHex) {
    return adjustHexLightness(baseHex, hash % 2 === 0 ? 0.06 : -0.06);
  }
  return adjusted;
}

/** Stable mapped-app color from catalog identity — independent of list composition. */
export function resolveStableMappedColor(
  app: AppColorInput,
  brandMap: Readonly<Record<string, string>>,
): string | undefined {
  const brand = resolveBrandColorHex(app, brandMap);
  if (!brand) return undefined;

  const colorKey = resolveAppColorKey(app);
  if (!colorKey) return brand;

  if (catalogKeysSharingHex(brandMap, brand).length <= 1) return brand;

  return brandColorVariant(brand, appColorKey(app));
}

function pickDistinctColor(key: string, used: Set<string>): string {
  const start = stableHash(key) % APP_USAGE_PALETTE.length;
  for (let offset = 0; offset < APP_USAGE_PALETTE.length; offset++) {
    const candidate = APP_USAGE_PALETTE[(start + offset) % APP_USAGE_PALETTE.length]!;
    if (!used.has(candidate)) return candidate;
  }
  return APP_USAGE_PALETTE[start]!;
}

function reserveMappedColor(color: string, app: AppColorInput, used: Set<string>): string {
  if (!used.has(color)) return color;
  let attempt = 1;
  while (attempt < 8) {
    const candidate = brandColorVariant(color, `${appColorKey(app)}:dedupe:${attempt}`);
    if (!used.has(candidate)) return candidate;
    attempt++;
  }
  return color;
}

/** Assign brand colors from Convex map when available, else distinct palette fallback. */
export function assignBrandAppColors<T extends AppColorInput>(
  apps: T[],
  brandMap?: Readonly<Record<string, string>>,
  limit = 5,
): T[] {
  const count = Math.min(apps.length, limit);
  if (count === 0) return [...apps];

  const used = new Set<string>();
  const assigned: Array<string | undefined> = new Array(count);

  for (let i = 0; i < count; i++) {
    if (!brandMap) continue;
    const mapped = resolveStableMappedColor(apps[i]!, brandMap);
    if (!mapped) continue;
    const color = reserveMappedColor(mapped, apps[i]!, used);
    assigned[i] = color;
    used.add(color);
  }

  for (let i = 0; i < count; i++) {
    if (assigned[i]) continue;
    const native = normalizeAppColor(apps[i]!.color);
    if (native && !used.has(native)) {
      assigned[i] = native;
      used.add(native);
    }
  }

  for (let i = 0; i < count; i++) {
    if (assigned[i]) continue;
    const color = pickDistinctColor(appColorKey(apps[i]!), used);
    assigned[i] = color;
    used.add(color);
  }

  return apps.map((app, index) => {
    if (index >= count || !assigned[index]) return app;
    return { ...app, color: assigned[index] };
  });
}

/** Assign pairwise-distinct colors for the displayed app list (default top 5). */
export function assignDistinctAppColors<T extends AppColorInput>(apps: T[], limit = 5): T[] {
  return assignBrandAppColors(apps, undefined, limit);
}
